import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  BookCheck, Calendar, CalendarClock, CalendarDays, CalendarRange,
  Clock, History, Repeat, Inbox, AlertCircle, X, ChevronDown, ChevronUp
} from 'lucide-react';
import Tooltip from '../common/Tooltip';
import CategoryIcon from '../common/CategoryIcon';
import SectionLabel from '../common/SectionLabel';
import useAuth from '../../hooks/useAuth';
import useToast from '../../hooks/useToast';

const formatGerman = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (isNaN(d)) return '';
  return new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return format(d, 'yyyy-MM-dd');
};

const DebitModal = ({ transaction, onDebit, onClose }) => {
  const [customDate, setCustomDate] = useState('');
  const plannedDate = transaction.date;

  const book = (date) => {
    onDebit({ ...transaction, date });
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); onClose(); }} />
      <div
        className="absolute top-7 -left-4 translate-x-0 translate-y-0 z-50 bg-white rounded-2xl shadow-sm shadow-primary-900 border border-primary-100 w-72 max-w-[calc(100vw-2rem)] origin-top-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-1 right-0 p-2">
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-4 py-3 pt-6 space-y-3">
          {plannedDate && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => book(plannedDate)}
                className="w-full px-3 py-2 text-xs font-semibold bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-xl transition-colors"
              >
                Geplant: {formatGerman(plannedDate)}
              </button>
            </div>
          )}
          <div className="flex gap-1.5">
            {[
              { n: 0, label: 'Heute' },
              { n: 1, label: 'Gestern' },
              { n: 2, label: 'Vorgestern' }
            ].map(({ n, label }) => (
              <button
                key={n}
                type="button"
                onClick={() => book(daysAgo(n))}
                className="flex-1 px-2 py-2 text-xs font-medium bg-neutral-100 text-neutral-700 hover:bg-neutral-200 rounded-xl transition-colors"
              >
                {label}
              </button>
            ))}
          </div>

          {/* Custom date picker */}
          <div
            className="relative w-full group cursor-pointer"
            onClick={() => document.getElementById(`debit-date-${transaction.id}`).showPicker()}
          >
            <input
              type="text"
              readOnly
              placeholder="anderes Datum wählen..."
              value={formatGerman(customDate)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-xl outline-none text-xs text-neutral-700 bg-white cursor-pointer"
            />
            <input
              id={`debit-date-${transaction.id}`}
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value || '')}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
          </div>
          {customDate && (
            <button
              type="button"
              onClick={() => book(customDate)}
              className="w-full py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold rounded-xl transition-colors"
            >
              Mit {formatGerman(customDate)} buchen
            </button>
          )}
        </div>
      </div>
    </>
  );
};

const TransactionRow = ({ transaction, onEdit, onUpdate, topCategories, isUpcoming, onDebit }) => {
  const { apiFetch } = useAuth();
  const { showToast } = useToast();
  const { id, name, date, description, amount, categories,
        is_booked, is_reserved, is_planned, interval, end_date } = transaction;
  const [showDebitModal, setShowDebitModal] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(name);
  const [showFastAdd, setShowFastAdd] = useState(false);

  const handleFastAddCategory = async (category) => {
    try {
      const response = await apiFetch(`/api/transactions/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name, amount, date, description,
          is_booked, is_reserved, is_planned, interval, end_date,
          categories: [{ ...category, split_amount: amount }]
        })
      });
      if (response.ok) {
        onUpdate();
      } else {
        showToast('Kategorie konnte nicht hinzugefügt werden.');
      }
    } catch (err) {
      console.error('Error fast-adding category:', err);
      showToast('Kategorie konnte nicht hinzugefügt werden.');
    }
  };

  const startEditingName = () => {
    setNameDraft(name);
    setIsEditingName(true);
  };

  const saveNameEdit = async () => {
    const trimmed = nameDraft.trim();
    setIsEditingName(false);
    if (!trimmed || trimmed === name) return;

    try {
      const response = await apiFetch(`/api/transactions/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: trimmed, amount, date, description,
          is_booked, is_reserved, is_planned, interval, end_date,
          categories
        })
      });
      if (response.ok) {
        onUpdate();
      } else {
        showToast('Name konnte nicht geändert werden.');
      }
    } catch (err) {
      console.error('Error renaming transaction:', err);
      showToast('Name konnte nicht geändert werden.');
    }
  };

  const isExpense = amount < 0;
  const amountClass = isExpense ? 'text-primary-700' : 'text-emerald-600';
  const opacity = isUpcoming ? 'opacity-60' : '';

  return (
    <li className="group relative transition-all animate-row-enter">
      <div
        className="flex px-4 py-3 rounded-xl hover:bg-neutral-50 transition-all cursor-pointer"
        onClick={() => onEdit(transaction)}
      >
        {/* Left: Status indicator + name + date */}
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="flex flex-col">
              {isEditingName ? (
                <input
                  type="text"
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.target.select()}
                  onBlur={saveNameEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') { setNameDraft(name); setIsEditingName(false); }
                  }}
                  className={`font-semibold text-sm text-neutral-800 bg-neutral-50 border border-primary-300 rounded-md px-1.5 py-0.5 -mx-1.5 -my-0.5 outline-none ring-2 ring-primary-200 max-w-[200px] ${opacity}`}
                />
              ) : (
                <span
                    className={`font-semibold text-sm text-neutral-800 truncate cursor-pointer hover:text-primary-700 transition-colors ${opacity}`}
                    onClick={(e) => { e.stopPropagation(); startEditingName(); }}
                >
                {name}
              </span>
              )}
              {description && (
                  <span className="text-xs truncate max-w-[150px] text-primary-700 opacity-60">{description}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-0.5">
            {isUpcoming && (
                <div className="relative flex-shrink-0">
                  <button
                      onClick={(e) => { e.stopPropagation(); setShowDebitModal(true); }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-neutral-500 bg-neutral-100 hover:bg-emerald-100 hover:text-emerald-700 rounded-md transition-colors"
                  >
                    <BookCheck size={11} />
                    Buchen
                  </button>
                  {showDebitModal && (
                      <DebitModal
                          transaction={transaction}
                          onDebit={onDebit}
                          onClose={() => setShowDebitModal(false)}
                      />
                  )}
                </div>
            )}
            <span className={`text-xs text-neutral-400 ${opacity}`}>
              {isUpcoming
                ? date
                  ? `Geplant ${format(parseISO(date), 'd. MMM', { locale: de })}`
                  : 'Vorgemerkt'
                : date
                  ? format(parseISO(date), 'd. MMM yyyy', { locale: de })
                  : 'Kein Datum'}
            </span>
          </div>
        </div>

        {/* Right: amount + categories */}
        <div className="flex flex-col items-end flex-shrink-0 pl-3">
          <span className={`text-sm font-bold tabular-nums ${amountClass} ${opacity}`}>
            {isExpense ? '−' : '+'}{Math.abs(amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
          </span>
          <div className="flex gap-1.5 mt-1 flex-wrap justify-end">
            {categories.length > 0 ? (
              categories.map((cat, index) => (
                <Tooltip key={cat.id || index} content={cat.name} position="left">
                  <CategoryIcon
                    name={cat.icon_name}
                    size={17}
                    className={isExpense ? 'text-primary-500' : 'text-emerald-500'}
                  />
                </Tooltip>
              ))
            ) : (
              <div
                className="flex items-center gap-1 text-red-400 group-hover:text-red-600 transition-colors"
                onClick={topCategories.length > 0 ? (e) => { e.stopPropagation(); setShowFastAdd(prev => !prev); } : undefined}
              >
                <AlertCircle size={16} />
                <span className="text-xs">Fehlt</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fast-add category: hover on desktop, tap the Fehlt badge on touch */}
      {categories.length === 0 && topCategories.length > 0 && (
          <div className={`absolute right-4 top-14 z-50 transition-all duration-100 ease-in group-hover:duration-200 group-hover:ease-out ${
            showFastAdd
              ? 'opacity-100 visible pointer-events-auto'
              : 'opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto'
          }`}>
          <div className="flex flex-col items-end gap-1">
            {topCategories.map(cat => (
              <button
                key={cat.id}
                onClick={(e) => { e.stopPropagation(); setShowFastAdd(false); handleFastAddCategory(cat); }}
                className="flex items-center space-x-1 px-2 py-1 bg-white border border-neutral-200 rounded-md text-xs text-neutral-600 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-all shadow-md hover:scale-105"
              >
                <CategoryIcon name={cat.icon_name} size={13} />
                <span>{cat.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </li>
  );
};

const TransactionGroup = ({ title, items, icon, defaultCollapsed = false, rowProps }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  if (!items || items.length === 0) return null;

  return (
    <div className="mb-5">
      <button
        type="button"
        onClick={() => setCollapsed(prev => !prev)}
        className="w-full flex items-center justify-between gap-1.5 px-2 py-1.5 sticky top-0 backdrop-blur-sm z-10"
      >
        <span className="text-xs font-bold text-neutral-400 uppercase tracking-widest flex items-center gap-1.5">
          {icon && React.createElement(icon, { size: 12 })}
          {title}
          <span className="text-neutral-300 normal-case font-semibold">({items.length})</span>
        </span>
        {collapsed ? <ChevronDown size={13} className="text-neutral-300" /> : <ChevronUp size={13} className="text-neutral-300" />}
      </button>
      {!collapsed && (
        <ul className="bg-white rounded-2xl border border-neutral-100 shadow-sm divide-y divide-neutral-50">
          {items.map(t => (
            <TransactionRow
              key={`${t.id}-${t.date || 'nodate'}-${t.is_booked ? 'b' : 'p'}`}
              transaction={t}
              onEdit={rowProps.onEdit}
              onUpdate={rowProps.onUpdate}
              onDebit={rowProps.onDebit}
              topCategories={rowProps.topCategories}
              isUpcoming={!t.is_booked}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const TransactionList = ({ groups = {}, onEdit, onUpdate, onDebit, transactions = [], monthEndBalance = null, monthEndBalanceLabel = '' }) => {
  const topCategories = React.useMemo(() => {
    const counts = {};
    const catMap = {};
    transactions.filter(t => t.amount < 0).forEach(t => {
      t.categories.forEach(cat => {
        counts[cat.id] = (counts[cat.id] || 0) + 1;
        if (!catMap[cat.id]) catMap[cat.id] = { id: cat.id, name: cat.name, icon_name: cat.icon_name };
      });
    });
    return Object.keys(counts)
      .sort((a, b) => counts[b] - counts[a])
      .slice(0, 5)
      .map(id => catMap[id]);
  }, [transactions]);

  const hasAny = (
    groups.upcoming?.length > 0 ||
    groups.today?.length > 0 ||
    groups.yesterday?.length > 0 ||
    groups.last7Days?.length > 0 ||
    groups.last14Days?.length > 0 ||
    groups.currentMonth?.length > 0 ||
    Object.keys(groups.history || {}).some(k => groups.history[k].length > 0)
  );

  const monthEndBalanceBar = monthEndBalance !== null && (
    <div className="flex justify-between items-center bg-white px-4 py-3 rounded-2xl border border-neutral-100 shadow-sm mb-3">
      <SectionLabel as="span">
        Kontostand Ende {monthEndBalanceLabel}
      </SectionLabel>
      <span className={`text-base font-extrabold tabular-nums ${monthEndBalance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
        {monthEndBalance.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
      </span>
    </div>
  );

  if (!hasAny) {
    return (
      <div>
        {monthEndBalanceBar}
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mb-4">
            <Inbox size={30} className="text-neutral-300" />
          </div>
          <p className="text-neutral-400 font-medium">Keine Einträge gefunden.</p>
          <p className="text-neutral-300 text-sm mt-1">Versuche die Filter anzupassen.</p>
        </div>
      </div>
    );
  }

  const rowProps = { onEdit, onUpdate, onDebit, topCategories };

  return (
    <div className="space-y-1">
      {monthEndBalanceBar}
      <TransactionGroup title="Anstehend" items={groups.upcoming} icon={CalendarClock} rowProps={rowProps} />
      <TransactionGroup title="Heute" items={groups.today} icon={Calendar} rowProps={rowProps} />
      <TransactionGroup title="Gestern" items={groups.yesterday} icon={Clock} rowProps={rowProps} />
      <TransactionGroup title="Letzte 7 Tage" items={groups.last7Days} icon={History} rowProps={rowProps} />
      <TransactionGroup title="Letzte 14 Tage" items={groups.last14Days} icon={Repeat} rowProps={rowProps} />
      <TransactionGroup title="Aktueller Monat" items={groups.currentMonth} icon={CalendarDays} rowProps={rowProps} />
      {Object.entries(groups.history || {}).map(([dateLabel, items], index) => (
        <TransactionGroup
          key={dateLabel}
          title={dateLabel}
          items={items}
          icon={CalendarRange}
          defaultCollapsed={index > 0}
          rowProps={rowProps}
        />
      ))}
    </div>
  );
};

export default TransactionList;
